import Dexie, { Table } from 'dexie';

// Category types
export type Category =
  | 'Income'
  | 'Credit Card'
  | 'Loan'
  | 'Subscription'
  | 'Vehicle'
  | 'Property'
  | 'Rent/Mortgage'
  | 'Other';

export const CATEGORIES: Category[] = [
  'Income',
  'Credit Card',
  'Loan',
  'Subscription',
  'Vehicle',
  'Property',
  'Rent/Mortgage',
  'Other',
];

// Entry interface
export interface BudgetEntry {
  id?: number;
  category: Category;
  title: string;
  value: number;
  date: Date;
  type: 'income' | 'expense';
  isRecurring?: boolean;
  recurringFrequency?: 'weekly' | 'monthly' | 'yearly';
  createdAt: Date;
  updatedAt: Date;
}

// Credit Card interface
export interface CreditCard {
  id?: number;
  name: string;
  balance: number;
  interestRate: number; // Annual percentage rate
  minimumPayment?: number;
  createdAt: Date;
  updatedAt: Date;
}

// Database class
export class BudgetDatabase extends Dexie {
  entries!: Table<BudgetEntry>;
  creditCards!: Table<CreditCard>;

  constructor() {
    super('BudgetDatabase');
    this.version(1).stores({
      entries: '++id, category, date, type, createdAt',
    });
    // Version 2: Add credit cards and recurring fields
    this.version(2).stores({
      entries: '++id, category, date, type, createdAt, isRecurring',
      creditCards: '++id, name, createdAt',
    });
  }
}

// Create database instance
export const db = new BudgetDatabase();

// Helper functions for database operations
export const dbOperations = {
  // Add new entry
  addEntry: async (entry: Omit<BudgetEntry, 'id' | 'createdAt' | 'updatedAt'>): Promise<number> => {
    const now = new Date();
    return await db.entries.add({
      ...entry,
      createdAt: now,
      updatedAt: now,
    });
  },

  // Get all entries
  getAllEntries: async (): Promise<BudgetEntry[]> => {
    return await db.entries.orderBy('date').reverse().toArray();
  },

  // Get entries by category
  getEntriesByCategory: async (category: Category): Promise<BudgetEntry[]> => {
    return await db.entries.where('category').equals(category).toArray();
  },

  // Get entries by type (income/expense)
  getEntriesByType: async (type: 'income' | 'expense'): Promise<BudgetEntry[]> => {
    return await db.entries.where('type').equals(type).toArray();
  },

  // Get entries for a specific month
  getEntriesByMonth: async (year: number, month: number): Promise<BudgetEntry[]> => {
    const startDate = new Date(year, month, 1);
    const endDate = new Date(year, month + 1, 0, 23, 59, 59);
    
    return await db.entries
      .where('date')
      .between(startDate, endDate, true, true)
      .toArray();
  },

  // Update entry
  updateEntry: async (id: number, updates: Partial<BudgetEntry>): Promise<number> => {
    return await db.entries.update(id, {
      ...updates,
      updatedAt: new Date(),
    });
  },

  // Delete entry
  deleteEntry: async (id: number): Promise<void> => {
    await db.entries.delete(id);
  },

  // Get summary for a specific month
  getMonthlySummary: async (year: number, month: number) => {
    const entries = await dbOperations.getEntriesByMonth(year, month);
    
    const totalIncome = entries
      .filter(e => e.type === 'income')
      .reduce((sum, e) => sum + e.value, 0);
    
    const totalExpenses = entries
      .filter(e => e.type === 'expense')
      .reduce((sum, e) => sum + e.value, 0);
    
    const balance = totalIncome - totalExpenses;
    
    return {
      totalIncome,
      totalExpenses,
      balance,
      entryCount: entries.length,
    };
  },

  // Clear all entries (for development/testing)
  clearAllEntries: async (): Promise<void> => {
    await db.entries.clear();
  },
};

// Credit Card operations
export const creditCardOperations = {
  // Add new credit card
  addCard: async (card: Omit<CreditCard, 'id' | 'createdAt' | 'updatedAt'>): Promise<number> => {
    const now = new Date();
    return await db.creditCards.add({
      ...card,
      createdAt: now,
      updatedAt: now,
    });
  },

  // Get all credit cards
  getAllCards: async (): Promise<CreditCard[]> => {
    return await db.creditCards.orderBy('name').toArray();
  },

  // Update credit card
  updateCard: async (id: number, updates: Partial<CreditCard>): Promise<number> => {
    return await db.creditCards.update(id, {
      ...updates,
      updatedAt: new Date(),
    });
  },

  // Delete credit card
  deleteCard: async (id: number): Promise<void> => {
    await db.creditCards.delete(id);
  },

  // Calculate monthly payment needed to pay off in X months
  calculatePayoffPlan: (balance: number, interestRate: number, months: number) => {
    const monthlyRate = interestRate / 100 / 12;
    if (monthlyRate === 0) {
      return balance / months;
    }
    const monthlyPayment = (balance * monthlyRate * Math.pow(1 + monthlyRate, months)) / 
                          (Math.pow(1 + monthlyRate, months) - 1);
    return monthlyPayment;
  },

  // Calculate total interest paid
  calculateTotalInterest: (balance: number, interestRate: number, monthlyPayment: number) => {
    let remainingBalance = balance;
    let totalInterest = 0;
    let months = 0;
    const monthlyRate = interestRate / 100 / 12;

    while (remainingBalance > 0 && months < 360) { // Max 30 years
      const interestCharge = remainingBalance * monthlyRate;
      totalInterest += interestCharge;
      const principalPayment = monthlyPayment - interestCharge;
      remainingBalance -= principalPayment;
      months++;

      if (monthlyPayment <= interestCharge) {
        return { totalInterest: Infinity, months: Infinity }; // Payment too low
      }
    }

    return { totalInterest, months };
  },

  // Clear all credit cards
  clearAllCards: async (): Promise<void> => {
    await db.creditCards.clear();
  },
};
